import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {MaybeNativeDatePicker} from "@khan/component-date-picker";
import {mediaQueries} from "@khan/shared-styles";

import type {DateRange} from "./types.ts";

interface Props {
    onConfirm: (arg1: DateRange) => void;
    startDate: Date | null | undefined;
    endDate: Date | null | undefined;
    maxDate?: moment.Moment | null | undefined;
    inputAriaLabel?: string;
}

// If the given date is after the maxDate, clamp the date to the max value.
const maybeClampDate = (
    date: moment.Moment,
    maxDate: moment.Moment | null | undefined,
): moment.Moment => {
    if (maxDate && date.isAfter(maxDate)) {
        return maxDate;
    }
    return date;
};

export const DateRangeModal = ({
    onConfirm,
    startDate,
    endDate,
    maxDate,
    inputAriaLabel,
}: Props): React.ReactElement => {
    const [start, setStart] = useState(
        maybeClampDate(startDate ? moment(startDate) : moment(), maxDate),
    );
    const [end, setEnd] = useState(
        maybeClampDate(
            endDate ? moment(endDate) : moment().add(1, "months"),
            maxDate,
        ),
    );

    const setRange = (startDate: moment.Moment, endDate: moment.Moment) => {
        setStart(startDate);
        setEnd(endDate);
    };

    const handleSetEndDate = (value?: moment.Moment | null) => {
        if (!value) {
            return;
        }

        if (value.isAfter(start)) {
            setEnd(value);
        } else {
            setRange(value, value);
        }
    };

    const handleSetStartDate = (value?: moment.Moment | null | undefined) => {
        if (!value) {
            return;
        }

        if (value.isBefore(end)) {
            setStart(value);
        } else {
            setRange(value, value);
        }
    };

    const handleConfirm = () => {
        onConfirm({
            endDate: end.clone().toDate(),
            startDate: start.clone().toDate(),
        });
    };

    const renderDatePicker = (
        selectedDate: moment.Moment | null | undefined,
        onChange: (value?: moment.Moment | null | undefined) => void,
        id: string,
        inputAriaLabel?: string,
    ): React.ReactNode => {
        return (
            <LabelMedium>
                <MaybeNativeDatePicker
                    selectedDate={selectedDate}
                    updateDate={onChange}
                    dateFormat="MMMM D, YYYY"
                    style={{width: "100%"}}
                    id={id}
                    maxDate={maxDate}
                    inputAriaLabel={inputAriaLabel}
                />
            </LabelMedium>
        );
    };

    const renderModalContent = (): React.ReactElement => {
        return (
            <div className={css(styles.modalContent)}>
                <LabelLarge style={styles.label}>{t`From`}</LabelLarge>
                {renderDatePicker(
                    start,
                    handleSetStartDate,
                    "start-date-field",
                    t`Choose or enter a start date`,
                )}
                <div className={css(styles.spacer)} />
                <LabelLarge style={styles.label}>{t`To`}</LabelLarge>
                {renderDatePicker(
                    end,
                    handleSetEndDate,
                    "end-date-field",
                    t`Choose or enter an end date`,
                )}
            </div>
        );
    };

    return (
        <OnePaneDialog
            title={t`Set custom range`}
            content={renderModalContent()}
            footer={
                <Button onClick={handleConfirm} style={styles.btn}>
                    {t`Confirm`}
                </Button>
            }
            style={styles.modal}
        />
    );
};

const styles = StyleSheet.create({
    label: {
        color: semanticColor.core.foreground.neutral.subtle,
        marginBlockEnd: sizing.size_080,
    },
    modal: {
        width: "90%",
        maxWidth: 388,
        height: "90%",
        maxHeight: 372,

        [mediaQueries.smOrSmaller]: {
            width: "100%",
            maxWidth: "none",
            height: "100%",
            maxHeight: "none",
        },
    },
    btn: {
        minInlineSize: 138,
    },
    spacer: {
        height: sizing.size_240,
    },
    modalContent: {
        marginBlockStart: -sizing.size_080,
        marginBlockEnd: sizing.size_160,
    },
});

export default DateRangeModal;
